import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../utils/axios';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

const ExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalExpense, setTotalExpense] = useState(0);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [isIncome, setIsIncome] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  // 下拉刷新相关状态
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const navigate = useNavigate();
  // 账单列表ref，用于获取滚动位置
  const billListRef = React.useRef(null);
  // 记账相关状态
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [commonDescriptions, setCommonDescriptions] = useState([]);
  // 当前选择的月份和年份
  const [currentMonth, setCurrentMonth] = useState(dayjs().month());
  const [currentYear, setCurrentYear] = useState(dayjs().year());
  // 显示月份选择器
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  // 搜索功能
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  // 手机版日期选择器状态
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerYear, setDatePickerYear] = useState(dayjs().year());
  const [datePickerMonth, setDatePickerMonth] = useState(dayjs().month());

  // 获取数据（只拉取当前月份，避免拉取全部数据导致卡顿）
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const selectedMonth = dayjs().year(currentYear).month(currentMonth);
      const startOfMonth = selectedMonth.startOf('month').format('YYYY-MM-DD');
      const endOfMonth = selectedMonth.endOf('month').format('YYYY-MM-DD');

      // 并行获取分类和当月账目
      const [categoriesResponse, expensesResponse] = await Promise.all([
        api.get('/expenses/categories'),
        api.get('/expenses', {
          params: {
            start_date: startOfMonth,
            end_date: endOfMonth + ' 23:59:59',
            limit: 500
          }
        })
      ]);

      if (categoriesResponse.data?.success) {
        setCategories(categoriesResponse.data.data || []);
      }

      if (expensesResponse.data?.success) {
        const data = expensesResponse.data.data.expenses || [];
        const incomeData = data.filter(item => item.type === 'income');
        const expenseData = data.filter(item => item.type === 'expense');

        setIncomes(incomeData);
        setExpenses(expenseData);

        const incTotal = incomeData.reduce((sum, item) => sum + (typeof item.amount === 'number' ? item.amount : parseFloat(item.amount) || 0), 0);
        const expTotal = expenseData.reduce((sum, item) => sum + (typeof item.amount === 'number' ? item.amount : parseFloat(item.amount) || 0), 0);
        setTotalIncome(incTotal);
        setTotalExpense(expTotal);
      }
    } catch (error) {
      console.error('获取数据失败:', error);
    } finally {
      setLoading(false);
    }
  }, [currentMonth, currentYear]);

  // 初始化数据
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 搜索：直接过滤当前已加载的数据，不再请求全量
  const fetchAllData = useCallback(() => {
    setIsSearching(!!searchQuery);
  }, [searchQuery]);

  // 按日期分组数据（useMemo 缓存，避免每次渲染重新计算）
  const groupedData = useMemo(() => {
    let allData = [...incomes, ...expenses];

    // 应用搜索过滤
    if (isSearching && searchQuery) {
      const q = searchQuery.toLowerCase();
      allData = allData.filter(item => {
        const desc = item.description || '';
        const category = categories.find(c => c.id === item.category_id)?.name || '';
        return desc.toLowerCase().includes(q) || category.toLowerCase().includes(q);
      });
    }

    const grouped = {};
    allData.forEach(item => {
      const dateKey = dayjs(item.date).format('YYYY-MM-DD');
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(item);
    });

    return Object.entries(grouped)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([date, items]) => {
        const sortedItems = items.sort((a, b) => {
          const timeA = new Date(a.created_at || a.date).getTime();
          const timeB = new Date(b.created_at || b.date).getTime();
          return timeB - timeA;
        });
        return [date, sortedItems];
      });
  }, [incomes, expenses, isSearching, searchQuery, categories]);

  // 获取分类信息（useCallback 缓存）
  const getCategoryById = useCallback((id) => {
    return categories.find(cat => cat.id === id);
  }, [categories]);

  // 分类汇总（取金额前4的分类，useMemo 缓存）
  const topCategories = useMemo(() => {
    const allData = [...incomes, ...expenses];
    return categories
      .map(category => {
        const total = allData
          .filter(item => item.category_id === category.id)
          .reduce((sum, item) => sum + (typeof item.amount === 'number' ? item.amount : parseFloat(item.amount) || 0), 0);
        return { ...category, total };
      })
      .sort((a, b) => b.total - a.total)
      .slice(0, 4);
  }, [categories, incomes, expenses]);

  // 处理记账按钮点击
  const handleAddExpense = () => {
    setShowAddExpense(true);
    setSelectedCategory(null);
    setAmount('');
    setDescription('');
    setIsIncome(false);
  };

  // 处理分类选择
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    
    // 计算该分类的常用用途，不限制时间，考虑所有数据
    const categoryExpenses = [...incomes, ...expenses]
      .filter(item => item.category_id === category.id);
    
    // 统计每个用途的使用次数
    const descriptionCount = {};
    categoryExpenses.forEach(item => {
      const desc = item.description || '';
      if (desc) {
        descriptionCount[desc] = (descriptionCount[desc] || 0) + 1;
      }
    });
    
    // 转换为数组并按使用次数排序
    const sortedDescriptions = Object.entries(descriptionCount)
      .map(([description, count]) => ({ description, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20); // 显示前20个常用值
    
    setCommonDescriptions(sortedDescriptions);
  };

  // 处理完成按钮点击
  const handleComplete = async () => {
    if (!selectedCategory || !amount) return;

    setLoading(true);
    try {
      const currentTime = dayjs().format('HH:mm:ss');
      const selectedDateTime = `${selectedDate} ${currentTime}`;
      const expenseData = {
        amount: parseFloat(amount),
        description: description || selectedCategory.name,
        category_id: selectedCategory.id,
        date: selectedDateTime,
        type: isIncome ? 'income' : 'expense',
        tags: []
      };

      const response = await api.post('/expenses', expenseData);
      if (response.data?.success) {
        const newId = response.data.data?.id;
        const amt = parseFloat(amount);

        // 用提交数据构造新记录（后端只返回 id）
        const newRecord = {
          id: newId,
          amount: amt,
          description: description || selectedCategory.name,
          category_id: selectedCategory.id,
          category_name: selectedCategory.name,
          date: selectedDateTime,
          type: isIncome ? 'income' : 'expense',
          created_by: null,
          created_by_name: localStorage.getItem('username') || '我',
          created_at: dayjs().format('YYYY-MM-DD HH:mm:ss')
        };

        // 本地更新，不重新拉取数据
        if (isIncome) {
          setIncomes(prev => [newRecord, ...prev]);
          setTotalIncome(prev => prev + amt);
        } else {
          setExpenses(prev => [newRecord, ...prev]);
          setTotalExpense(prev => prev + amt);
        }

        // 重置表单
        setShowAddExpense(false);
        setSelectedCategory(null);
        setAmount('');
        setDescription('');
        setIsIncome(false);
        setSelectedDate(dayjs().format('YYYY-MM-DD'));
      }
    } catch (error) {
      console.error('保存记账失败:', error);
      alert('保存失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 处理取消按钮点击
  const handleCancel = () => {
    setShowAddExpense(false);
    setSelectedCategory(null);
    setAmount('');
    setDescription('');
    setIsIncome(false);
    setSelectedDate(dayjs().format('YYYY-MM-DD'));
    setShowDatePicker(false);
  };

  // 手机版日期选择器相关函数
  const getDaysInMonth = (year, month) => {
    return dayjs(`${year}-${month + 1}-01`).daysInMonth();
  };

  const getFirstDayOfMonth = (year, month) => {
    return dayjs(`${year}-${month + 1}-01`).day();
  };

  const getWeekDays = () => {
    return ['日', '一', '二', '三', '四', '五', '六'];
  };

  const getMonths = () => {
    return ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'];
  };

  const selectDate = (day) => {
    const date = dayjs(`${datePickerYear}-${datePickerMonth + 1}-${day}`).format('YYYY-MM-DD');
    setSelectedDate(date);
    setShowDatePicker(false);
  };

  const prevMonth = () => {
    if (datePickerMonth === 0) {
      setDatePickerYear(datePickerYear - 1);
      setDatePickerMonth(11);
    } else {
      setDatePickerMonth(datePickerMonth - 1);
    }
  };

  const nextMonth = () => {
    if (datePickerMonth === 11) {
      setDatePickerYear(datePickerYear + 1);
      setDatePickerMonth(0);
    } else {
      setDatePickerMonth(datePickerMonth + 1);
    }
  };

  // 滑动状态管理
  const [swipeState, setSwipeState] = useState({});

  // 处理删除记账条目
  const handleDeleteExpense = async (itemId) => {
    if (!window.confirm('确定要删除这条记账吗？')) return;
    setLoading(true);
    try {
      const response = await api.delete(`/expenses/${itemId}`);
      if (response.data?.success) {
        // 本地移除，不重新拉取
        setIncomes(prev => prev.filter(i => i.id !== itemId));
        setExpenses(prev => prev.filter(i => i.id !== itemId));
        setSwipeState(prev => {
          const next = { ...prev };
          delete next[itemId];
          return next;
        });
      }
    } catch (error) {
      console.error('删除记账失败:', error);
      alert('删除失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 滑动事件处理
  const handleSwipeStart = (e, itemId) => {
    e.stopPropagation();
    setSwipeState(prev => ({
      ...prev,
      [itemId]: {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        isSwiping: true
      }
    }));
  };

  const handleSwipeMove = (e, itemId) => {
    e.stopPropagation();
    const state = swipeState[itemId];
    if (!state || !state.isSwiping) return;

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const deltaX = currentX - state.startX;
    const deltaY = currentY - state.startY;

    // 只处理水平滑动
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      e.preventDefault();
      // 限制滑动距离，最大-100px（向左滑动100px）
      const newOffset = Math.max(-100, deltaX);
      setSwipeState(prev => ({
        ...prev,
        [itemId]: {
          ...state,
          offset: newOffset
        }
      }));
    }
  };

  const handleSwipeEnd = (e, itemId) => {
    e.stopPropagation();
    const state = swipeState[itemId];
    if (!state) return;

    // 计算最终偏移量
    let finalOffset = 0;
    if (state.offset && state.offset < -50) {
      // 滑动超过50px，显示删除按钮
      finalOffset = -100;
    }

    setSwipeState(prev => ({
      ...prev,
      [itemId]: {
        ...state,
        offset: finalOffset,
        isSwiping: false
      }
    }));
  };

  // 重置滑动状态
  const resetSwipe = (itemId) => {
    setSwipeState(prev => ({
      ...prev,
      [itemId]: {
        offset: 0,
        isSwiping: false
      }
    }));
  };

  // 处理类型切换
  const handleTypeChange = (type) => {
    setIsIncome(type === 'income');
  };

  // 处理更多按钮点击
  const handleMoreClick = () => {
    navigate('/mobile/categories', { state: { currentYear, currentMonth } });
  };

  // 下拉刷新相关函数
  const handleTouchStart = (e) => {
    // 简化检测，只要触摸开始就记录位置
    setStartY(e.touches[0].clientY);
    setIsPulling(true);
  };

  const handleTouchMove = (e) => {
    if (!isPulling || isRefreshing) return;

    const touchY = e.touches[0].clientY;
    const diff = touchY - startY;

    // 检查是否在顶部并且向下拉动
    const isAtTop = window.scrollY === 0 || (billListRef.current && billListRef.current.scrollTop === 0);
    
    // 只有在顶部并且向下拉动才触发
    if (isAtTop && diff > 0) {
      setCurrentY(Math.min(diff, 80)); // 最大拉动距离80px
      e.preventDefault(); // 阻止默认滚动
    }
  };

  const handleTouchEnd = async () => {
    if (!isPulling) return;

    // 如果拉动距离足够，触发刷新
    if (currentY > 50) {
      setIsRefreshing(true);
      try {
        await fetchData();
      } finally {
        setIsRefreshing(false);
      }
    }

    // 重置状态
    setCurrentY(0);
    setIsPulling(false);
  };

  return (
    <div 
      style={{
        width: '100%',
        maxWidth: '100%',
        margin: '0',
        background: '#f5f5f5',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        position: 'relative',
        boxSizing: 'border-box'
      }}
    >
      {/* 下拉刷新指示器 */}
      {currentY > 0 && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: currentY,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'rgba(255, 218, 87, 0.1)',
          zIndex: 1000,
        }}>
          <div style={{
            fontSize: '14px',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
          }}>
            {isRefreshing ? (
              <>
                <div style={{ 
                  width: '16px', 
                  height: '16px', 
                  border: '2px solid #333', 
                  borderTop: '2px solid transparent', 
                  borderRadius: '50%', 
                  marginRight: '8px',
                  animation: 'spin 1s linear infinite'
                }}></div>
                刷新中...
              </>
            ) : (
              '下拉刷新'
            )}
          </div>
        </div>
      )}
      
      {/* 添加CSS动画 */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* 隐藏日期选择器左侧的日期显示 */
        .date-picker-dropdown .ant-picker-date-panel-left {
          display: none;
        }
        
        /* 让日期选择网格占据整个宽度 */
        .date-picker-dropdown .ant-picker-date-panel {
          width: 100% !important;
        }
        
        .date-picker-dropdown .ant-picker-date-panel-body {
          width: 100% !important;
        }
      `}</style>
      {!showAddExpense ? (
        loading ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            height: '100vh',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#f5f5f5'
          }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              border: '3px solid #f3f3f3', 
              borderTop: '3px solid #ffda57', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite'
            }}></div>
            <div style={{ marginTop: '16px', fontSize: '16px', color: '#333' }}>加载中...</div>
          </div>
        ) : (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            transform: currentY > 0 ? `translateY(${currentY}px)` : 'none',
            transition: isRefreshing ? 'transform 0.3s ease' : 'none'
          }}>
            {/* 顶部导航栏 */}
            <div style={{
              background: 'rgba(255, 218, 87, 1)',
              color: '#333',
              padding: '20px 16px 10px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>

              {/* 搜索框 */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: '#fff',
                  borderRadius: '8px',
                  padding: '8px 12px',
                }}>
                  <input
                    type="text"
                    placeholder="搜索记账记录..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      fontSize: '14px',
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        setIsSearching(true);
                        fetchAllData();
                      }
                    }}
                  />
                  {searchQuery && (
                    <button
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#999',
                      }}
                      onClick={() => {
                        setSearchQuery('');
                        setIsSearching(false);
                      }}
                    >
                      ×
                    </button>
                  )}
                  <button
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#333',
                      marginLeft: '8px',
                    }}
                    onClick={() => {
                      if (searchQuery) {
                        setIsSearching(true);
                        fetchAllData();
                      }
                    }}
                  >
                    🔍
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', marginBottom: '16px', height: '80px' }}>
                {/* 月份部分 - 占用20%宽度 */}
                <div 
                  style={{ 
                    width: '20%', 
                    paddingRight: '10px', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowMonthPicker(!showMonthPicker)}
                >
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>{String(currentMonth + 1).padStart(2, '0')}月</div>
                  <div style={{ fontSize: '14px', color: '#666' }}>{currentYear}年</div>
                </div>
                
                {/* 分割线 - 占高度的50%垂直居中显示 */}
                <div style={{ 
                  width: '1px', 
                  background: '#cdcdcd', 
                  marginRight: '10px',
                  height: '50%',
                  alignSelf: 'center'
                }}></div>
                
                {/* 收支部分 - 占用80%宽度 */}
                <div style={{ width: '80%', display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                  {/* 收入部分 - 居中显示 */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ fontSize: '16px', color: '#333', fontWeight: 'bold' }}>收入</div>
                    <div style={{ fontSize: 'clamp(16px, 4vw, 24px)', fontWeight: 'bold', color: '#52c41a' }}>¥{totalIncome.toFixed(2)}</div>
                  </div>
                  {/* 支出部分 - 居中显示 */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ fontSize: '16px', color: '#333', fontWeight: 'bold' }}>支出</div>
                    <div style={{ fontSize: 'clamp(16px, 4vw, 24px)', fontWeight: 'bold', color: '#ff4d4f' }}>¥{totalExpense.toFixed(2)}</div>
                  </div>
                  {/* 结余部分 - 居中显示 */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ fontSize: '16px', color: '#333', fontWeight: 'bold' }}>结余</div>
                    <div style={{ fontSize: 'clamp(16px, 4vw, 24px)', fontWeight: 'bold', color: totalIncome - totalExpense >= 0 ? '#52c41a' : '#ff4d4f' }}>¥{(totalIncome - totalExpense).toFixed(2)}</div>
                  </div>
                </div>
              </div>
              
              {/* 分类导航 */}
              <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'nowrap' }}>
                {topCategories.map(category => (
                  <div key={category.id} style={{ textAlign: 'center', flex: 1, marginBottom: '10px' }}>
                    <div style={{ fontSize: '14px', marginBottom: '4px', fontWeight: 'bold' }}>{category.name}</div>
                    <div style={{ fontSize: '12px', color: '#999' }}>¥{category.total.toFixed(2)}</div>
                  </div>
                ))}
                {categories.length > 4 && (
                  <div 
                    style={{ textAlign: 'center', flex: 1, marginBottom: '10px', cursor: 'pointer' }}
                    onClick={handleMoreClick}
                  >
                    <div style={{ fontSize: '14px', marginBottom: '4px', fontWeight: 'bold' }}>更多</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>查看全部</div>
                  </div>
                )}
              </div>
            </div>
            
            {/* 月份选择器 */}
            {showMonthPicker && (
              <div style={{
                background: '#fff',
                padding: '15px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                marginBottom: '10px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px'
                }}>
                  <button 
                    style={{
                      padding: '8px 16px',
                      borderRadius: '4px',
                      border: '1px solid #e8e8e8',
                      background: '#f5f5f5',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setCurrentYear(currentYear - 1);
                    }}
                  >
                    上一年
                  </button>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{currentYear}年</div>
                  <button 
                    style={{
                      padding: '8px 16px',
                      borderRadius: '4px',
                      border: '1px solid #e8e8e8',
                      background: '#f5f5f5',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setCurrentYear(currentYear + 1);
                    }}
                  >
                    下一年
                  </button>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '10px'
                }}>
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(month => (
                    <button
                      key={month}
                      style={{
                        padding: '12px',
                        borderRadius: '4px',
                        border: currentMonth === month ? '1px solid #ffda57' : '1px solid #e8e8e8',
                        background: currentMonth === month ? '#fff3cd' : '#f5f5f5',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onClick={() => {
                        setCurrentMonth(month);
                        setShowMonthPicker(false);
                      }}
                    >
                      {month + 1}月
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* 账单列表 */}
            <div 
              ref={billListRef}
              style={{ 
                flex: 1, 
                overflowY: 'auto', 
                padding: '10px',
                touchAction: 'pan-y'
              }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {groupedData.length > 0 ? (
                groupedData.map(([date, items]) => {
                  const formattedDate = dayjs(date).format('MM月DD日');
                  
                  const dayIncome = parseFloat(items.filter(item => item.type === 'income').reduce((sum, item) => sum + (typeof item.amount === 'number' ? item.amount : parseFloat(item.amount) || 0), 0));
                  const dayExpense = parseFloat(items.filter(item => item.type === 'expense').reduce((sum, item) => sum + (typeof item.amount === 'number' ? item.amount : parseFloat(item.amount) || 0), 0));
                  
                  return (
                    <div key={date} style={{ marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', padding: '0 5px' }}>
                        <div style={{ fontSize: '12px', color: '#999' }}>{formattedDate}</div>
                        <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: '#999' }}>
                          <span style={{ marginRight: '10px' }}>收入: ¥{dayIncome.toFixed(2)}</span>
                          <span>支出: ¥{dayExpense.toFixed(2)}</span>
                        </div>
                      </div>
                      {items.map(item => {
                        const category = getCategoryById(item.category_id);
                        return (
                          <div key={item.id} style={{
                            position: 'relative',
                            marginBottom: '8px',
                            overflow: 'hidden',
                            borderRadius: '8px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          }}>
                            {/* 滑动容器 */}
                            <div style={{
                              position: 'relative',
                              width: '100%',
                              height: '100%',
                            }}>
                              {/* 记账条目内容 */}
                              <div 
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '12px',
                                  background: '#fff',
                                  transform: `translateX(${swipeState[item.id]?.offset || 0}px)`,
                                  transition: swipeState[item.id]?.isSwiping ? 'none' : 'transform 0.3s ease',
                                  position: 'relative',
                                  zIndex: 2,
                                }}
                                onTouchStart={(e) => handleSwipeStart(e, item.id)}
                                onTouchMove={(e) => handleSwipeMove(e, item.id)}
                                onTouchEnd={(e) => handleSwipeEnd(e, item.id)}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <div style={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    borderRadius: '20px', 
                                    background: category?.color || '#1890ff',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    color: '#fff',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    padding: '2px',
                                    flexDirection: 'column'
                                  }}>
                                    {(() => {
                                      const name = category?.name || '未知';
                                      const displayName = name.length > 4 ? name.substring(0, 4) : name;
                                      
                                      if (displayName.length === 4) {
                                        return (
                                          <>
                                            <div>{displayName.substring(0, 2)}</div>
                                            <div>{displayName.substring(2, 4)}</div>
                                          </>
                                        );
                                      } else if (displayName.length === 3) {
                                        return (
                                          <>
                                            <div>{displayName.substring(0, 1)}</div>
                                            <div>{displayName.substring(1, 3)}</div>
                                          </>
                                        );
                                      } else {
                                        return displayName;
                                      }
                                    })()}
                                  </div>
                                  <div>
                                    <div>{item.description || category?.name || '未知'}</div>
                                    {item.created_at && (
                                      <div style={{ fontSize: '12px', color: '#999', marginTop: '3px', display: 'flex', alignItems: 'center' }}>
                                        {dayjs(item.created_at).format('HH:mm:ss')}
                                        <span style={{ marginLeft: '10px' }}>
                                          {item.created_by_name || item.created_by || '系统'}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div style={{ color: item.type === 'income' ? '#52c41a' : '#ff4d4f', fontWeight: 'bold', paddingRight: '20px' }}>
                                  {item.type === 'income' ? '+' : '-'}{(typeof item.amount === 'number' ? item.amount : parseFloat(item.amount) || 0).toFixed(2)}
                                </div>
                              </div>
                              
                              {/* 删除按钮 */}
                              <div style={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                bottom: 0,
                                width: '100px',
                                background: '#ff4d4f',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                color: '#fff',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                zIndex: 1,
                              }}
                              onClick={() => handleDeleteExpense(item.id)}
                              >
                                删除
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#999',
                  padding: '20px'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                  <div style={{ fontSize: '16px', marginBottom: '8px' }}>暂无记账记录</div>
                  <div style={{ fontSize: '14px', textAlign: 'center' }}>点击右下角的 + 按钮添加记账</div>
                </div>
              )}
            </div>
            
            {/* 浮动记账按钮 */}
            <div style={{
              position: 'fixed',
              bottom: '30px',
              right: '30px',
              zIndex: 1000,
            }}>
              <button
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '30px',
                  background: 'rgba(255, 218, 87, 1)',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(255, 218, 87, 0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '30px',
                  color: '#333',
                  cursor: 'pointer',
                  outline: 'none',
                }}
                onClick={handleAddExpense}
              >
                +
              </button>
            </div>
          </div>
        )
      ) : (
        /* 记账界面 */
        <div style={{ display: 'flex', flexDirection: 'column', background: '#fff' }}>
          {/* 顶部导航栏 */}
          <div style={{
            background: 'rgba(255, 218, 87, 1)',
            color: '#333',
            padding: '20px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{isIncome ? '收入' : '支出'}</div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <button 
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: '#333', 
                  fontSize: '16px',
                  padding: '5px 10px',
                }}
                onClick={() => handleTypeChange('expense')}
              >
                支出
              </button>
              <button 
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: '#333', 
                  fontSize: '16px',
                  padding: '5px 10px',
                }}
                onClick={() => handleTypeChange('income')}
              >
                收入
              </button>
              <button 
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: '#333', 
                  fontSize: '16px',
                  padding: '5px 10px',
                }}
                onClick={handleCancel}
              >
                取消
              </button>
            </div>
          </div>
          
          {/* 分类选择 */}
          {!selectedCategory ? (
            <div style={{ flex: 1, padding: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
                {categories.filter(cat => cat.type === (isIncome ? 'income' : 'expense')).map(category => (
                  <div 
                    key={category.id}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '15px 10px',
                      cursor: 'pointer',
                      textAlign: 'center',
                      boxSizing: 'border-box'
                    }}
                    onClick={() => handleCategorySelect(category)}
                  >
                    <div style={{ 
                      width: '50px', 
                      height: '50px', 
                      borderRadius: '25px', 
                      background: category.color || '#1890ff',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginBottom: '8px',
                      color: '#fff',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      padding: '2px',
                      flexDirection: 'column'
                    }}>
                      {(() => {
                        const displayName = category.name.length > 4 ? category.name.substring(0, 4) : category.name;
                        
                        if (displayName.length === 4) {
                          return (
                            <>
                              <div>{displayName.substring(0, 2)}</div>
                              <div>{displayName.substring(2, 4)}</div>
                            </>
                          );
                        } else if (displayName.length === 3) {
                          return (
                            <>
                              <div>{displayName.substring(0, 1)}</div>
                              <div>{displayName.substring(1, 3)}</div>
                            </>
                          );
                        } else {
                          return displayName;
                        }
                      })()}
                    </div>
                    <div style={{ fontSize: '12px', textAlign: 'center', wordBreak: 'break-all' }}>{category.name}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* 金额输入界面 */
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column' }}>
              {/* 选中的分类 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '0',
                height: '35px'
              }}>
                <div style={{ 
                  width: '35px', 
                  height: '35px', 
                  borderRadius: '17.5px', 
                  background: selectedCategory.color || '#1890ff',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  padding: '2px',
                  flexDirection: 'column'
                }}>
                  {(() => {
                    const displayName = selectedCategory.name.length > 4 ? selectedCategory.name.substring(0, 4) : selectedCategory.name;
                    
                    if (displayName.length === 4) {
                      return (
                        <>
                          <div>{displayName.substring(0, 2)}</div>
                          <div>{displayName.substring(2, 4)}</div>
                        </>
                      );
                    } else if (displayName.length === 3) {
                      return (
                        <>
                          <div>{displayName.substring(0, 1)}</div>
                          <div>{displayName.substring(1, 3)}</div>
                        </>
                      );
                    } else {
                      return displayName;
                    }
                  })()}
                </div>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{selectedCategory.name}</div>
              </div>
              
              {/* 金额显示 */}
              <div style={{ 
                fontSize: '48px', 
                fontWeight: 'bold', 
                textAlign: 'center', 
                marginBottom: '20px',
              }}>
                {isIncome ? '+' : '-'}{amount || '0.00'}
              </div>
              
              {/* 用途输入 */}
              <div style={{
                padding: '10px',
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                marginBottom: '15px',
              }}>
                <input
                  type="text"
                  placeholder="搜索或输入用途"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{
                    width: '100%',
                    border: 'none',
                    outline: 'none',
                    fontSize: '16px',
                  }}
                />
              </div>
              
              {/* 常用用途 */}
              {commonDescriptions.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontSize: '14px', marginBottom: '8px', color: '#666' }}>常用用途</div>
                  <div style={{ 
                    display: 'flex', 
                    overflowX: 'auto', 
                    gap: '8px',
                    paddingBottom: '8px',
                    scrollbarWidth: 'none', /* Firefox */
                    WebkitOverflowScrolling: 'touch' /* iOS */
                  }}>
                    {commonDescriptions.map((item, index) => (
                      <button
                        key={index}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '14px',
                          border: '1px solid #e8e8e8',
                          background: '#f5f5f5',
                          fontSize: '12px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => setDescription(item.description)}
                      >
                        {item.description}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* 时间选择 */}
              <div style={{ marginBottom: '15px' }}>
                <div style={{ fontSize: '14px', marginBottom: '8px', color: '#666' }}>选择日期</div>
                <div 
                  style={{
                    padding: '15px',
                    border: '1px solid #e8e8e8',
                    borderRadius: '8px',
                    fontSize: '18px',
                    textAlign: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setDatePickerYear(dayjs(selectedDate).year());
                    setDatePickerMonth(dayjs(selectedDate).month());
                    setShowDatePicker(true);
                  }}
                >
                  {dayjs(selectedDate).format('YYYY年MM月DD日')}
                </div>
              </div>
              
              {/* 数字键盘 */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' }}>
                {[7, 8, 9, 4, 5, 6, 1, 2, 3, '.', 0, '⌫'].map((key) => (
                  <button
                    key={key}
                    style={{
                      padding: '20px',
                      fontSize: '20px',
                      borderRadius: '8px',
                      border: '1px solid #e8e8e8',
                      background: '#f5f5f5',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      if (key === '⌫') {
                        setAmount(amount.slice(0, -1));
                      } else if (key === '.') {
                        if (!amount.includes('.')) {
                          setAmount(amount + key);
                        }
                      } else {
                        setAmount(amount + key);
                      }
                    }}
                  >
                    {key}
                  </button>
                ))}
              </div>
              
              {/* 完成按钮 */}
              <button
                style={{
                  padding: '15px',
                  backgroundColor: 'rgba(255, 218, 87, 1)',
                  color: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginTop: 'auto'
                }}
                onClick={handleComplete}
              >
                完成
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* 手机版日期选择器弹窗 */}
      {showDatePicker && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-end',
          zIndex: 1000
        }}
        onClick={() => setShowDatePicker(false)}
        >
          <div 
            style={{
              width: '100%',
              background: '#fff',
              borderRadius: '16px 16px 0 0',
              padding: '20px',
              maxHeight: '70vh',
              overflowY: 'auto',
              animation: 'slideUp 0.3s ease'
            }}
            onClick={(e) => e.stopPropagation()}
            >
            {/* 日期选择器头部 */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <button
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '20px',
                  border: 'none',
                  background: '#f5f5f5',
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
                onClick={() => setShowDatePicker(false)}
              >
                ✕
              </button>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {datePickerYear}年{getMonths()[datePickerMonth]}
              </div>
              <button
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '20px',
                  border: 'none',
                  background: '#f5f5f5',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setSelectedDate(dayjs().format('YYYY-MM-DD'));
                  setShowDatePicker(false);
                }}
              >
                今天
              </button>
            </div>
            
            {/* 年月快速选择 */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '10px',
              marginBottom: '15px',
              padding: '0 10px',
              boxSizing: 'border-box'
            }}>
              <select
                value={datePickerYear}
                onChange={(e) => setDatePickerYear(parseInt(e.target.value))}
                style={{
                  padding: '8px 10px',
                  border: '1px solid #e8e8e8',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: '#fff',
                  cursor: 'pointer',
                  width: '45%',
                  maxWidth: '140px',
                  boxSizing: 'border-box',
                  appearance: 'none'
                }}
              >
                {Array.from({ length: 10 }, (_, i) => 2020 + i).map((year) => (
                  <option key={year} value={year}>{year}年</option>
                ))}
              </select>
              <select
                value={datePickerMonth}
                onChange={(e) => setDatePickerMonth(parseInt(e.target.value))}
                style={{
                  padding: '8px 10px',
                  border: '1px solid #e8e8e8',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: '#fff',
                  cursor: 'pointer',
                  width: '45%',
                  maxWidth: '140px',
                  boxSizing: 'border-box',
                  appearance: 'none'
                }}
              >
                {getMonths().map((month, index) => (
                  <option key={index} value={index}>{month}</option>
                ))}
              </select>
            </div>
            
            {/* 星期标题 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              marginBottom: '10px'
            }}>
              {getWeekDays().map((day, index) => (
                <div 
                  key={index} 
                  style={{
                    textAlign: 'center',
                    fontSize: '14px',
                    color: '#999',
                    padding: '8px 0'
                  }}
                >
                  {day}
                </div>
              ))}
            </div>
            
            {/* 日期格子 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)'
            }}>
              {/* 空白填充 */}
              {Array.from({ length: getFirstDayOfMonth(datePickerYear, datePickerMonth) }).map((_, index) => (
                <div key={`empty-${index}`} style={{ padding: '12px 0' }} />
              ))}
              
              {/* 日期 */}
              {Array.from({ length: getDaysInMonth(datePickerYear, datePickerMonth) }).map((_, index) => {
                const day = index + 1;
                const currentDate = dayjs(`${datePickerYear}-${datePickerMonth + 1}-${day}`).format('YYYY-MM-DD');
                const isToday = dayjs(currentDate).isSame(dayjs(), 'day');
                const isSelected = currentDate === selectedDate;
                
                return (
                  <button
                    key={day}
                    style={{
                      padding: '12px 0',
                      border: 'none',
                      background: isSelected ? '#ffda57' : isToday ? '#fff3cd' : 'transparent',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      fontSize: '16px',
                      color: isSelected ? '#333' : isToday ? '#333' : '#333'
                    }}
                    onClick={() => selectDate(day)}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            
            <style>{`
              @keyframes slideUp {
                from {
                  transform: translateY(100%);
                }
                to {
                  transform: translateY(0);
                }
              }
            `}</style>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesPage;